import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Chip,
} from '@mui/material';
import {
    Print as PrintIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import WSButton from '../../../Components/Button/WSButton';
import { callAPI, formatCurrency, formatDate, Method, numberToWords } from '../../../commonFunctions/CommonFunctions';
import { useSnackbar } from '../../../Components/Snackbar/SnackBarContext';
import config from '../../../Config/config.json'

interface InvoiceItem {
    ItemId: string;
    ItemName: string;
    Quantity: number;
    Price: number;
    Total: number;
}

interface InvoiceData {
    InvoiceId: string;
    InvoiceNumber: string;
    CustomerId: string;
    CustomerName: string;
    Status: string;
    TotalAmount: number;
    Remarks: string;
    CreatedBy: string;
    CreatedByName: string;
    CreatedDate: string;
    items: InvoiceItem[];
}

const InvoicePrint: React.FC = () => {
    const { invoiceId } = useParams<{ invoiceId: string }>();
    console.log('params are', invoiceId)

    const componentRef = useRef<HTMLDivElement>(null);
    const { showMessage } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [InvoiceData, setInvoiceData] = useState<InvoiceData | null>(null);

    useEffect(() => {
        async function getDataById() {
            try {
                console.log('try to fetch invoice by id')
                const response: any = await callAPI("invoice", Method.GET, null, invoiceId);
                console.log("Quotation Data:", response);
                if (response.data) {
                    setInvoiceData(response.data);
                }
            } catch (error) {
                showMessage("Something went wrong. Please try again.", "error");
            } finally {
                setLoading(false);
            }
        }

        if (invoiceId) {
            getDataById();
        }
    }, [invoiceId]);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Quotation_${InvoiceData?.InvoiceNumber ?? ""}`,
        onAfterPrint: () => {
            console.log('Print completed');
        },
    });

    const handleDownloadPDF = async () => {
        const element = componentRef.current;
        if (!element) return;

        const canvas = await html2canvas(element, {
            scale: 2,
            logging: false,
            useCORS: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });

        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`Quotation_${InvoiceData?.InvoiceNumber}.pdf`);
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'approved':
                return { bg: '#dcfce7', color: '#166534' };
            case 'pending':
                return { bg: '#fef3c7', color: '#92400e' };
            case 'rejected':
                return { bg: '#fee2e2', color: '#991b1b' };
            default:
                return { bg: '#f3f4f6', color: '#374151' };
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f3f4f6', minHeight: '100vh' }}>
            {/* Action Buttons */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                {/* <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/quotations')}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    Back
                </Button> */}

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <WSButton
                        id='printBtn'
                        color='primary'
                        variant='outlined'
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        label='Print'
                    />
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadPDF}
                        color='secondary'
                    >
                        Download PDF
                    </Button>
                </Box>
            </Paper>

            {/* Printable Content */}
            <div ref={componentRef}>
                <Box
                    sx={{
                        width: '210mm',
                        minHeight: '297mm',
                        margin: '0 auto',
                        bgcolor: '#ffffff',
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                        '@media print': {
                            margin: 0,
                            padding: 0,
                            boxShadow: 'none',
                        },
                    }}
                >
                    {/* Print Content */}
                    <Box sx={{ p: 5 }}>
                        {/* Header */}
                        <Box sx={{ mb: 4, textAlign: 'center' }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: '#1f2937',
                                    mb: 1,
                                    fontSize: '28px',
                                }}
                            >
                                INVOICE
                            </Typography>
                            <Divider
                                sx={{
                                    width: '100px',
                                    margin: '0 auto',
                                    borderColor: '#6366f1',
                                    borderWidth: 2,
                                }}
                            />
                        </Box>

                        {/* Company Info & Quotation Details */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={6}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#374151',
                                            mb: 1,
                                        }}
                                    >
                                        Your Company Name
                                    </Typography>
                                    <Typography variant="body2" color="#6b7280">
                                        123 Business Street
                                    </Typography>
                                    <Typography variant="body2" color="#6b7280">
                                        City, State 123456
                                    </Typography>
                                    <Typography variant="body2" color="#6b7280">
                                        Phone: +91 12345 67890
                                    </Typography>
                                    <Typography variant="body2" color="#6b7280">
                                        Email: info@company.com
                                    </Typography>
                                    <Typography variant="body2" color="#6b7280">
                                        GST: 22AAAAA0000A1Z
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            bgcolor: '#f9fafb',
                                            borderRadius: '8px',
                                            display: 'inline-block',
                                            width: '100%',
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Invoice No:</strong> {InvoiceData?.InvoiceNumber ?? ""}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Date:</strong> {formatDate(InvoiceData?.CreatedDate ?? "")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Status:</strong>{' '}
                                            <Chip
                                                label={InvoiceData?.Status ?? ""}
                                                size="small"
                                                sx={{
                                                    bgcolor: getStatusColor(InvoiceData?.Status ?? "").bg,
                                                    color: getStatusColor(InvoiceData?.Status ?? "").color,
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Created By:</strong> {InvoiceData?.CreatedByName ?? ""}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Customer Details */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 4,
                                bgcolor: '#f9fafb',
                                borderRadius: '8px',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    mb: 2,
                                    borderLeft: '4px solid #6366f1',
                                    pl: 2,
                                }}
                            >
                                Bill To:
                            </Typography>
                            <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                                {InvoiceData?.CustomerName ?? ""}
                            </Typography>
                            <Typography variant="body2" color="#6b7280">
                                Customer ID: {InvoiceData?.CustomerId ?? ""}
                            </Typography>
                        </Paper>

                        {/* Items Table */}
                        <TableContainer component={Paper} elevation={0} sx={{ mb: 4, border: '1px solid #e5e7eb' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '14px' }}>Sl. No.</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '14px' }}>Item Name</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '14px' }}>Quantity</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '14px' }}>Price (₹)</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '14px' }}>Total (₹)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {InvoiceData?.items.map((item, index) => (
                                        <TableRow key={item.ItemId}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{item.ItemName}</TableCell>
                                            <TableCell align="right">{item.Quantity}</TableCell>
                                            <TableCell align="right">{formatCurrency(item.Price)}</TableCell>
                                            <TableCell align="right">{formatCurrency(item.Total)}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 600 }}>
                                            Subtotal:
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(InvoiceData?.TotalAmount ?? "")}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 600 }}>
                                            VAT {config.vat}%:
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(0)}</TableCell>
                                    </TableRow>
                                    <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 700, fontSize: '16px' }}>
                                            Grand Total:
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '16px', color: '#6366f1' }}>
                                            {formatCurrency(InvoiceData?.TotalAmount ?? "")}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Amount in Words */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 4,
                                bgcolor: '#fef3c7',
                                borderRadius: '8px',
                            }}
                        >
                            <Typography variant="body2">
                                <strong>Amount in Words:</strong>{' '}
                                {numberToWords(InvoiceData?.TotalAmount ?? 0)} Rupees Only
                            </Typography>
                        </Paper>

                        {/* Remarks */}
                        {InvoiceData?.Remarks && (
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#374151',
                                        mb: 1,
                                    }}
                                >
                                    Remarks / Terms & Conditions:
                                </Typography>
                                <Typography variant="body2" color="#6b7280">
                                    {InvoiceData.Remarks}
                                </Typography>
                                <Typography variant="body2" color="#6b7280" sx={{ mt: 1 }}>
                                    • Payment is due within 15 days of invoice date
                                </Typography>
                                <Typography variant="body2" color="#6b7280">
                                    • Goods once sold will not be taken back
                                </Typography>
                                <Typography variant="body2" color="#6b7280">
                                    • This is a computer generated invoice
                                </Typography>
                            </Box>
                        )}

                        {/* Footer */}
                        <Box sx={{ mt: 6, textAlign: 'center' }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body2" color="#9ca3af">
                                Thank you for your business!
                            </Typography>
                            <Typography variant="caption" color="#d1d5db">
                                This is a system generated invoice and requires no signature
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </div>
        </Box>
    );
};

export default InvoicePrint;